"use client";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { publicSans } from "@portal/app/fonts";
import {
  HEADING_FONT_STACK,
  SANS_FONT_STACK,
} from "@portal/app/_components/theme/options/typography";
import { LandingPageBlock } from "../../components/LandingPageBlock";
import type { ResolvedPageContentBlock } from "../../types";
import { PUCK_TABLET_MEDIA } from "../layoutGeometry";
import { PAGE_DESKTOP_MEDIA } from "../../constants";

type HeadingBlock = Extract<ResolvedPageContentBlock, { blockType: "heading" }>;

export function PageHeading({
  block,
  typography = "standard",
}: {
  block: HeadingBlock;
  typography?: "standard" | "campaign" | "intro" | "supporting";
}) {
  if (typography === "standard") return <LandingPageBlock block={block} />;
  const { text, eyebrow, description } = block.resolvedContent;
  const campaign = typography === "campaign";
  const supporting = typography === "supporting";
  const color =
    block.background === "brand"
      ? "#fff"
      : supporting
        ? "primary.main"
        : campaign
          ? "#0b2ea1"
          : "#262626";
  return (
    <Box sx={{ textAlign: block.align, minWidth: 0, pt: campaign ? "4px" : 0 }}>
      {eyebrow && (
        <Typography
          sx={{
            display: campaign ? "none" : "block",
            color,
            fontSize: 14,
            lineHeight: "28px",
            fontWeight: 600,
            letterSpacing: "1.6px",
            textTransform: "uppercase",
            mb: "8px",
            [PUCK_TABLET_MEDIA]: { fontSize: 15, letterSpacing: "1.4px", mb: "12px" },
            [PAGE_DESKTOP_MEDIA]: { display: "block" },
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        component={`h${block.level}`}
        sx={{
          fontFamily: campaign ? HEADING_FONT_STACK : SANS_FONT_STACK,
          fontSize: campaign ? 44 : supporting ? "1.5rem" : 28,
          lineHeight: campaign ? 1.05 : supporting ? 1.3 : "33.6px",
          fontWeight: campaign || supporting ? 700 : 600,
          letterSpacing: "normal",
          color,
          m: 0,
          [PUCK_TABLET_MEDIA]: campaign ? { fontSize: 48, lineHeight: 1.02 } : {},
          [PAGE_DESKTOP_MEDIA]: campaign ? { fontSize: 62 } : {},
        }}
      >
        {text}
      </Typography>
      {description && (
        <Typography
          sx={{
            maxWidth: campaign ? 720 : undefined,
            fontFamily: campaign ? publicSans.style.fontFamily : SANS_FONT_STACK,
            color: block.background === "brand" ? "#fff" : campaign ? "#2b3242" : "#262626",
            fontSize: 18,
            lineHeight: 1.5,
            mt: campaign ? "10px" : "8px",
            mb: 0,
            [PUCK_TABLET_MEDIA]: campaign ? { fontSize: 20, mt: "14px" } : {},
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}
